import { Classes, EditableText, Intent, Position } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { Tooltip } from 'vet-bones/components';
import { isEqual, isTesting } from 'vet-bones/utils';

import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';

export type EditableLabelProps = {
  className?: string;
  errorIntent?: Intent;
  value: string;
  /** whether to call onSetValue on every change or just after losing focus */
  setValueOnChange?: boolean;
  onBlur?: (newLabel: string) => void;
  onSetValue: (newLabel: string) => void;
  onValidate?: (newLabel: string) => string;
  disabled?: boolean;
  canSetEmptyString?: boolean;
  monospaceText?: boolean;
} & Pick<
  React.ComponentProps<typeof EditableText>,
  | 'confirmOnEnterKey'
  | 'maxLines'
  | 'minLines'
  | 'minWidth'
  | 'multiline'
  | 'placeholder'
>;

export const EditableLabel: React.VFC<EditableLabelProps> = ({
  className,
  confirmOnEnterKey,
  errorIntent = Intent.DANGER,
  value,
  setValueOnChange,
  onBlur,
  onSetValue,
  onValidate = () => '',
  placeholder,
  maxLines,
  minLines,
  multiline,
  minWidth,
  disabled = false,
  canSetEmptyString = false,
  monospaceText = false,
}) => {
  const [inputValue, setInputValue] = React.useState<string>(value || '');
  const [isHovered, setIsHovered] = React.useState(false);
  const didValueChange = useDidValueChange(value, isEqual);
  React.useEffect(() => {
    if (didValueChange) {
      setInputValue(value);
    }
  }, [didValueChange, value]);

  const [invalid, setInvalid] = React.useState('');

  const onCancel = React.useCallback(() => {
    setInputValue(value);
  }, [value]);

  const onChange = React.useCallback(
    (newValue: string) => {
      setInputValue(newValue);
      if (setValueOnChange) {
        onSetValue(newValue);
      }
    },
    [onSetValue, setValueOnChange]
  );

  const onConfirm = React.useCallback(
    (newValue: string) => {
      const newLabel = newValue.trim();
      const newInvalid = onValidate(newLabel);
      let blurValue = newValue;
      if (newLabel === '' && canSetEmptyString) {
        onSetValue(newLabel);
        blurValue = newLabel;
      } else if (newLabel && !newInvalid) {
        setInputValue(newLabel);
        onSetValue(newLabel);
        blurValue = newLabel;
      } else if (!setValueOnChange) {
        // Revert to the prior value if invalid or only empty string / whitespace is left
        setInputValue(value);
        blurValue = value;
      }
      onBlur?.(blurValue);
    },
    [onBlur, onSetValue, onValidate, setValueOnChange, value, canSetEmptyString]
  );

  React.useEffect(() => {
    setInvalid(onValidate(inputValue.trim()));
  }, [inputValue, onValidate]);

  return (
    <div
      className="bp4-popover-wrapper"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <EditableText
        // the way blueprintjs swaps an input for a span makes testing awkward,
        // so only use an input element when testing
        alwaysRenderInput={isTesting()}
        className={classNames(
          'sd-editable-label',
          className,
          monospaceText ? Classes.MONOSPACE_TEXT : null
        )}
        confirmOnEnterKey={confirmOnEnterKey}
        disabled={disabled}
        intent={invalid ? errorIntent : Intent.NONE}
        maxLines={maxLines}
        minLines={minLines}
        minWidth={minWidth}
        multiline={multiline}
        onCancel={onCancel}
        onChange={onChange}
        onConfirm={onConfirm}
        placeholder={placeholder}
        selectAllOnFocus
        value={inputValue}
      />
      <Tooltip
        autoFocus={false}
        content={invalid}
        disabled={!invalid}
        isOpen={Boolean(isHovered && invalid)}
        position={Position.BOTTOM}
        usePortal={!isTesting()}
      />
    </div>
  );
};
